// The folded rail's flyout beside one icon: when it opens and closes, and where the focus goes as it does.

import {
    useCallback,
    useEffect,
    useId,
    useRef,
    useState,
    type FocusEvent as ReactFocusEvent,
    type KeyboardEvent as ReactKeyboardEvent,
    type PointerEvent as ReactPointerEvent,
    type RefObject,
} from "react";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { handFocusBack, isHandingFocusBack } from "../../navigation/focusHandBack";
import { useLocation } from "../../navigation/history";
import { RAIL_BESIDE_PAGE_QUERY } from "../RailFrame";
import { FLYOUT_ITEM_SELECTOR, RAIL_COLUMN_SELECTOR, focusAfter, moveFlyoutFocus } from "./flyoutKeys";

/** How long a mouse rests on the icon before the flyout opens, so one passing over it opens nothing. */
export const FLYOUT_OPEN_DELAY_MS = 120;
/** How long the flyout waits for the pointer to come back before closing. */
export const FLYOUT_CLOSE_DELAY_MS = 200;
/** How far past the rail's border the flyout starts. */
export const FLYOUT_RAIL_GAP_PX = 3;

/**
 * The side offset that starts a popover `gap` px past the border of the rail column its anchor stands in. Measured,
 * not fixed: the folded nav scrolls, and a classic scrollbar narrows every link in it by its own width.
 */
export function offsetPastRail(anchor: HTMLElement, gap: number): number {
    const rail = anchor.closest<HTMLElement>(RAIL_COLUMN_SELECTOR);
    if (!rail) return gap;
    return Math.max(0, rail.getBoundingClientRect().right - anchor.getBoundingClientRect().right) + gap;
}

/**
 * Whether the browser shows this focus as a keyboard one. A script's focus after a mouse action is not, so a layer
 * handing the focus back does not open a preview; an engine that cannot say counts it as one.
 */
function shownAsKeyboardFocus(element: Element): boolean {
    try {
        return element.matches(":focus-visible");
    } catch {
        return true;
    }
}

interface OutsideEvent {
    target: EventTarget | null;
    preventDefault: () => void;
}

export interface RailFlyout {
    open: boolean;
    /** For the popover's content: where it starts beside the icon. */
    sideOffset: number;
    onOpenChange: (open: boolean) => void;
    anchorRef: RefObject<HTMLAnchorElement | null>;
    contentRef: RefObject<HTMLDivElement | null>;
    contentId: string;
    hintId: string;
    /** For the icon's link. */
    anchorProps: {
        ref: RefObject<HTMLAnchorElement | null>;
        "aria-expanded": boolean;
        "aria-controls": string | undefined;
        "aria-describedby": string;
        "aria-keyshortcuts": string;
        onPointerEnter: (event: ReactPointerEvent) => void;
        onPointerLeave: (event: ReactPointerEvent) => void;
        onPointerDown: () => void;
        onMouseDown: () => void;
        onClickCapture: () => void;
        onFocus: (event: ReactFocusEvent) => void;
        onBlur: () => void;
        onKeyDown: (event: ReactKeyboardEvent) => void;
    };
    /** For the popover's content. */
    contentProps: {
        ref: RefObject<HTMLDivElement | null>;
        id: string;
        onPointerEnter: (event: ReactPointerEvent) => void;
        onPointerLeave: (event: ReactPointerEvent) => void;
        onKeyDown: (event: ReactKeyboardEvent) => void;
        onOpenAutoFocus: (event: Event) => void;
        onCloseAutoFocus: (event: Event) => void;
        onEscapeKeyDown: (event: KeyboardEvent) => void;
        onInteractOutside: (event: OutsideEvent) => void;
        onFocusOutside: (event: OutsideEvent) => void;
    };
    /** Closes it; with `focusAnchor`, hands the focus back to the icon without that focus opening it again. */
    close: (options?: { focusAnchor?: boolean }) => void;
}

/**
 * A mouse resting on the icon opens it, a keyboard focus on the icon opens it as a preview, → moves the focus in.
 * The content is portalled out of the rail, so the keys that leave it are taken by hand.
 */
export function useRailFlyout(): RailFlyout {
    const [open, setOpen] = useState(false);
    const [sideOffset, setSideOffset] = useState(FLYOUT_RAIL_GAP_PX);
    const anchorRef = useRef<HTMLAnchorElement | null>(null);
    const contentRef = useRef<HTMLDivElement | null>(null);
    const contentId = useId();
    const hintId = useId();
    const openTimer = useRef<number | null>(null);
    const closeTimer = useRef<number | null>(null);
    // A focus a press brought, and one any layer hands back, are not a keyboard arriving.
    const pressed = useRef(false);
    // → on a closed flyout: the focus goes in once the content has mounted.
    const focusInOnMount = useRef(false);

    const clearTimers = useCallback(() => {
        if (openTimer.current !== null) window.clearTimeout(openTimer.current);
        if (closeTimer.current !== null) window.clearTimeout(closeTimer.current);
        openTimer.current = null;
        closeTimer.current = null;
    }, []);

    useEffect(() => clearTimers, [clearTimers]);

    const focusInside = useCallback(() => {
        const content = contentRef.current;
        if (!content) return false;
        const target =
            content.querySelector<HTMLElement>(`${FLYOUT_ITEM_SELECTOR}[aria-current="true"]`)
            ?? content.querySelector<HTMLElement>(FLYOUT_ITEM_SELECTOR);
        target?.focus();
        return true;
    }, []);

    const show = useCallback(() => {
        if (anchorRef.current) setSideOffset(offsetPastRail(anchorRef.current, FLYOUT_RAIL_GAP_PX));
        setOpen(true);
    }, []);

    const close = useCallback(
        ({ focusAnchor = false }: { focusAnchor?: boolean } = {}) => {
            clearTimers();
            focusInOnMount.current = false;
            setOpen(false);
            const anchor = anchorRef.current;
            if (!focusAnchor || !anchor) return;
            handFocusBack(() => anchor.focus());
        },
        [clearTimers],
    );

    const onOpenChange = useCallback((next: boolean) => (next ? show() : close()), [show, close]);

    const { pathname, search } = useLocation();
    const place = `${pathname}${search}`;
    const shownPlace = useRef(place);
    useEffect(() => {
        if (shownPlace.current === place) return;
        shownPlace.current = place;
        close();
    }, [place, close]);

    // The rail is hidden below md, not unmounted, and the portalled flyout would be left at the screen's corner.
    const besidePage = useMediaQuery(RAIL_BESIDE_PAGE_QUERY);
    useEffect(() => {
        if (!besidePage) close();
    }, [besidePage, close]);

    const focusIsInside = () => contentRef.current?.contains(document.activeElement) ?? false;

    const scheduleOpen = () => {
        if (closeTimer.current !== null) window.clearTimeout(closeTimer.current);
        closeTimer.current = null;
        if (open || openTimer.current !== null) return;
        openTimer.current = window.setTimeout(() => {
            openTimer.current = null;
            show();
        }, FLYOUT_OPEN_DELAY_MS);
    };

    const scheduleClose = () => {
        if (openTimer.current !== null) window.clearTimeout(openTimer.current);
        openTimer.current = null;
        if (!open || closeTimer.current !== null) return;
        closeTimer.current = window.setTimeout(() => {
            closeTimer.current = null;
            if (!focusIsInside()) close();
        }, FLYOUT_CLOSE_DELAY_MS);
    };

    const isMouse = (event: ReactPointerEvent) => event.pointerType === "mouse";

    const markPressed = () => {
        pressed.current = true;
    };

    // The icon is outside the portalled content, and a press or a focus there must not dismiss it.
    const keepForAnchor = (event: OutsideEvent) => {
        const target = event.target;
        if (target instanceof Node && anchorRef.current?.contains(target)) event.preventDefault();
    };

    return {
        open,
        sideOffset,
        onOpenChange,
        anchorRef,
        contentRef,
        contentId,
        hintId,
        anchorProps: {
            ref: anchorRef,
            "aria-expanded": open,
            "aria-controls": open ? contentId : undefined,
            "aria-describedby": hintId,
            "aria-keyshortcuts": "ArrowRight",
            onPointerEnter: (event) => {
                if (isMouse(event)) scheduleOpen();
            },
            onPointerLeave: (event) => {
                if (isMouse(event)) scheduleClose();
            },
            // A tap's focus comes after its pointerup, on the mouse events a touch emulates, and before its click.
            onPointerDown: markPressed,
            onMouseDown: markPressed,
            onClickCapture: () => {
                pressed.current = false;
            },
            onFocus: (event) => {
                const brought =
                    pressed.current || isHandingFocusBack() || !shownAsKeyboardFocus(event.currentTarget);
                pressed.current = false;
                if (!brought) show();
            },
            onBlur: () => {
                pressed.current = false;
            },
            onKeyDown: (event) => {
                if (event.key !== "ArrowRight" || event.altKey || event.ctrlKey || event.metaKey) return;
                event.preventDefault();
                if (open && focusInside()) return;
                focusInOnMount.current = true;
                show();
            },
        },
        contentProps: {
            ref: contentRef,
            id: contentId,
            onPointerEnter: (event) => {
                if (isMouse(event)) scheduleOpen();
            },
            onPointerLeave: (event) => {
                if (isMouse(event)) scheduleClose();
            },
            onKeyDown: (event) => {
                if (event.key === "ArrowLeft" && !event.altKey && !event.ctrlKey && !event.metaKey) {
                    event.preventDefault();
                    close({ focusAnchor: true });
                    return;
                }
                if (event.key === "Tab" && !event.altKey && !event.ctrlKey && !event.metaKey) {
                    event.preventDefault();
                    if (event.shiftKey) {
                        close({ focusAnchor: true });
                        return;
                    }
                    const anchor = anchorRef.current;
                    close();
                    if (anchor) focusAfter(anchor);
                    return;
                }
                if (contentRef.current) moveFlyoutFocus(event, contentRef.current);
            },
            onOpenAutoFocus: (event) => {
                event.preventDefault();
                if (!focusInOnMount.current) return;
                focusInOnMount.current = false;
                focusInside();
            },
            onCloseAutoFocus: (event) => event.preventDefault(),
            onEscapeKeyDown: (event) => {
                event.preventDefault();
                close({ focusAnchor: focusIsInside() });
            },
            onInteractOutside: keepForAnchor,
            onFocusOutside: keepForAnchor,
        },
        close,
    };
}
